import { useState, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { supabase } from './lib/supabase'
import api from './services/api'
import Login from './pages/Login'
import Pipeline from './pages/Pipeline'
import Companies from './pages/Companies'
import Contacts from './pages/Contacts'
import Relatorios from './pages/Relatorios'
import Usuarios from './pages/Usuarios'
import Produtos from './pages/Produtos'
import Dashboard from './pages/Dashboard'
import Agenda from './pages/Agenda'

function Notificacoes({ profile }) {
  const { user } = useAuth()
  const isMaster = profile?.user_role === 'master'
  const [open, setOpen] = useState(false)
  const [notifs, setNotifs] = useState([])
  const ref = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!user) return
    const params = {}
    if (isMaster) {
      params.is_master = true
    } else {
      params.is_master = false
      params.company_id = profile?.company_id
    }
    api.get('/deals', { params }).then(r => {
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)
      const amanha = new Date(hoje)
      amanha.setDate(amanha.getDate() + 1)
      const depoisAmanha = new Date(amanha)
      depoisAmanha.setDate(depoisAmanha.getDate() + 1)

      const relevantes = r.data.filter(d => {
        if (!d.data_agendamento || d.status !== 'open') return false
        const data = new Date(d.data_agendamento + 'T12:00:00')
        return data >= hoje && data < depoisAmanha
      }).map(d => ({
        ...d,
        dataObj: new Date(d.data_agendamento + 'T12:00:00')
      })).sort((a, b) => a.dataObj - b.dataObj)

      setNotifs(relevantes)
    })
  }, [user, profile, isMaster]) // eslint-disable-line

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const amanha = new Date(hoje)
  amanha.setDate(amanha.getDate() + 1)

  const isHoje = (d) => d.dataObj >= hoje && d.dataObj < amanha
  const isAmanha = (d) => d.dataObj >= amanha

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative bg-gray-700 hover:bg-gray-600 transition text-sm px-2 py-1.5 rounded-lg"
        title="Notificações">
        🔔
        {notifs.length > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {notifs.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-700 flex justify-between items-center">
            <p className="font-semibold text-sm">🔔 Compromissos</p>
            <button
              onClick={() => { setOpen(false); navigate('/agenda') }}
              className="text-xs text-indigo-400 hover:text-indigo-300 transition">
              Ver agenda →
            </button>
          </div>

          {notifs.length === 0 ? (
            <div className="px-4 py-6 text-center text-gray-500 text-sm">
              <p>✅ Nenhum compromisso hoje ou amanhã.</p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {notifs.map(deal => (
                <div key={deal.id}
                  className={`px-4 py-3 border-b border-gray-800 hover:bg-gray-800 transition cursor-pointer`}
                  onClick={() => { setOpen(false); navigate('/agenda') }}>
                  <div className="flex items-center gap-2 mb-1">
                    {isHoje(deal)
                      ? <span className="text-xs font-bold text-cyan-400 bg-cyan-900/40 px-2 py-0.5 rounded-full">🔔 Hoje</span>
                      : <span className="text-xs font-bold text-indigo-400 bg-indigo-900/40 px-2 py-0.5 rounded-full">📆 Amanhã</span>
                    }
                    {deal.produto && <span className="text-xs text-gray-400">{deal.produto}</span>}
                  </div>
                  <p className="text-sm font-semibold text-white truncate">{deal.title}</p>
                  <div className="flex gap-3 mt-1 text-xs text-gray-500">
                    {deal.contato && <span>👤 {deal.contato}</span>}
                    {deal.localizacao && <span>📍 {deal.localizacao}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Layout() {
  const { profile, signOut } = useAuth()
  const isMaster = profile?.user_role === 'master'
  const [menuOpen, setMenuOpen] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwords, setPasswords] = useState({ nova: '', confirmar: '' })
  const [pwMsg, setPwMsg] = useState('')
  const [pwLoading, setPwLoading] = useState(false)

  const handleChangePassword = async () => {
    setPwMsg('')
    if (!passwords.nova || !passwords.confirmar) return setPwMsg('Preencha todos os campos.')
    if (passwords.nova.length < 6) return setPwMsg('A senha deve ter pelo menos 6 caracteres.')
    if (passwords.nova !== passwords.confirmar) return setPwMsg('As senhas não coincidem.')
    setPwLoading(true)
    const { error } = await supabase.auth.updateUser({ password: passwords.nova })
    setPwLoading(false)
    if (error) return setPwMsg('Erro: ' + error.message)
    setPwMsg('✅ Senha alterada com sucesso!')
    setPasswords({ nova: '', confirmar: '' })
    setTimeout(() => { setShowPasswordModal(false); setPwMsg('') }, 1500)
  }

  const linkClass = ({ isActive }) =>
    isActive
      ? 'text-indigo-400 font-semibold border-b-2 border-indigo-400 pb-1'
      : 'text-gray-400 hover:text-white transition pb-1'

  const mobileLinkClass = ({ isActive }) =>
    isActive
      ? 'block px-4 py-3 text-indigo-400 font-semibold bg-indigo-900/30 rounded-lg'
      : 'block px-4 py-3 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition'

  const inputClass = "w-full bg-gray-800 rounded-lg px-3 py-2 mb-3 text-sm outline-none focus:ring-2 ring-indigo-500"

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <nav className="bg-gray-800 border-b border-gray-700 px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <img src="/logo.png" alt="Luuk CRM" className="h-14 object-contain" />

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            <NavLink to="/dashboard" className={linkClass}>Dashboard</NavLink>
            <NavLink to="/" end className={linkClass}>Pipeline</NavLink>
            <NavLink to="/agenda" className={linkClass}>Agenda</NavLink>
            <NavLink to="/relatorios" className={linkClass}>Relatórios</NavLink>
            <NavLink to="/produtos" className={linkClass}>Produtos</NavLink>
            {isMaster && <NavLink to="/companies" className={linkClass}>Empresas</NavLink>}
            {isMaster && <NavLink to="/contacts" className={linkClass}>Contatos</NavLink>}
            {isMaster && <NavLink to="/usuarios" className={linkClass}>Usuários</NavLink>}
          </div>

          {/* Desktop right */}
          <div className="hidden md:flex items-center gap-3">
            <Notificacoes profile={profile} />
            <button onClick={() => setShowPasswordModal(true)} className="text-right hover:opacity-80 transition" title="Alterar senha">
              <p className="text-sm text-white font-medium">{profile?.name} {isMaster && '👑'}</p>
              <p className="text-xs text-gray-400">{isMaster ? 'Master' : 'Cliente'}</p>
            </button>
            <button onClick={() => setShowPasswordModal(true)} className="bg-gray-700 hover:bg-gray-600 transition text-sm px-2 py-1.5 rounded-lg" title="Alterar senha">⚙️</button>
            <button onClick={signOut} className="bg-gray-700 hover:bg-red-600 transition text-sm px-3 py-1.5 rounded-lg text-gray-300 hover:text-white">Sair</button>
          </div>

          {/* Mobile right */}
          <div className="flex md:hidden items-center gap-2">
            <Notificacoes profile={profile} />
            <button onClick={() => setShowPasswordModal(true)} className="text-gray-400 hover:text-white p-2">⚙️</button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="text-gray-400 hover:text-white p-2 text-2xl">
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden mt-3 border-t border-gray-700 pt-3 space-y-1">
            <NavLink to="/dashboard" className={mobileLinkClass} onClick={() => setMenuOpen(false)}>📊 Dashboard</NavLink>
            <NavLink to="/" end className={mobileLinkClass} onClick={() => setMenuOpen(false)}>🔀 Pipeline</NavLink>
            <NavLink to="/agenda" className={mobileLinkClass} onClick={() => setMenuOpen(false)}>📅 Agenda</NavLink>
            <NavLink to="/relatorios" className={mobileLinkClass} onClick={() => setMenuOpen(false)}>📈 Relatórios</NavLink>
            <NavLink to="/produtos" className={mobileLinkClass} onClick={() => setMenuOpen(false)}>📦 Produtos</NavLink>
            {isMaster && <NavLink to="/companies" className={mobileLinkClass} onClick={() => setMenuOpen(false)}>🏢 Empresas</NavLink>}
            {isMaster && <NavLink to="/contacts" className={mobileLinkClass} onClick={() => setMenuOpen(false)}>👥 Contatos</NavLink>}
            {isMaster && <NavLink to="/usuarios" className={mobileLinkClass} onClick={() => setMenuOpen(false)}>👤 Usuários</NavLink>}
            <div className="px-4 py-3 border-t border-gray-700 mt-2 flex items-center justify-between">
              <div>
                <p className="text-sm text-white font-medium">{profile?.name} {isMaster && '👑'}</p>
                <p className="text-xs text-gray-400">{isMaster ? 'Master' : 'Cliente'}</p>
              </div>
              <button onClick={signOut} className="bg-gray-700 hover:bg-red-600 transition text-sm px-3 py-1.5 rounded-lg text-gray-300 hover:text-white">Sair</button>
            </div>
          </div>
        )}
      </nav>

      {/* Modal Alterar Senha */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 rounded-xl p-6 w-full max-w-sm border border-gray-700">
            <h2 className="text-lg font-bold mb-1">🔐 Alterar Senha</h2>
            <p className="text-gray-400 text-xs mb-4">Mínimo de 6 caracteres</p>
            <input className={inputClass} type="password" placeholder="Nova senha *" value={passwords.nova} onChange={e => setPasswords({ ...passwords, nova: e.target.value })} />
            <input className={inputClass} type="password" placeholder="Confirmar nova senha *" value={passwords.confirmar} onChange={e => setPasswords({ ...passwords, confirmar: e.target.value })} />
            {pwMsg && <p className={`text-xs mb-3 ${pwMsg.startsWith('Erro') || pwMsg.startsWith('Preencha') || pwMsg.startsWith('A senha') || pwMsg.startsWith('As senhas') ? 'text-red-400' : 'text-emerald-400'}`}>{pwMsg}</p>}
            <div className="flex gap-2">
              <button onClick={handleChangePassword} disabled={pwLoading} className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 py-2 rounded-lg text-sm font-semibold transition">{pwLoading ? 'Salvando...' : 'Salvar'}</button>
              <button onClick={() => { setShowPasswordModal(false); setPwMsg(''); setPasswords({ nova: '', confirmar: '' }) }} className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-sm transition">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      <main className="p-4 md:p-6">
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/" element={<Pipeline />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/relatorios" element={<Relatorios />} />
          <Route path="/produtos" element={<Produtos />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/usuarios" element={<Usuarios />} />
        </Routes>
      </main>
    </div>
  )
}

function AppRoutes() {
  const { user, loading } = useAuth()
  if (loading) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <img src="/logo.png" alt="Luuk CRM" className="h-12 animate-pulse" />
    </div>
  )
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
        <Route path="/*" element={user ? <Layout /> : <Navigate to="/login" />} />
      </Routes>
    </BrowserRouter>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}